import {
  IActivationCodeDialogResponse,
  IAutoRenewRequest,
  IAutoRenewUpdateRequest,
  IGetPaymentMethodsResponse,
  IPaymentMethod,
  IPayOutstandingInvoicesRequest,
  ISubscription,
} from "@/common/api/unifiedPortal/interfaces";
import {
  hideExpiredTrial,
  hideRenewOrUpgradeLinkForLimitedPurchaseSub,
  isNotActiveYet,
  isSafeProduct,
  processSubscriptionDataForDisplay,
  showDanger,
  showWarning,
} from "@/common/subscriptionHelper";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { processNotifications } from "./ProcessNotifications";
import { useNotificationsStore } from "@/stores/notifications";
import { logEvent, logException } from "@/common/logger";
import { getProductDisplayNameBySku, IProductDisplayRequest } from "@/common/productsBySku";
import i18n from "@/i18n";
import { AxiosError } from "axios";
import { handleApiError } from "@/common/handleApiError";
import { unifiedApi } from "@/common";
import { useSubscriptionPageStore } from "@/stores/SubscriptionPage";
import { useSiteSettingsStore } from "@/stores/siteSettings";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import { getUserDetailsFromStore } from "@/common/userDetails";
import { getBuyRouteName } from "../Buy/commonFn";
import { pageStates, paymentTypes } from "../Buy/BuyEnums";
import { useUserStore } from "@/stores/user";
import { useBuyFlowStore } from "@/stores/buyFlow";
import {
  ExpirationState,
  KeyCodeSeller,
  payPalCheckOutPage,
  pageState as subscriptionPageState,
  ZuoraSubscriptionStatus,
} from "@/globalEnums";
import router from "@/routes";
import { createPayPalAgreementToken } from "../Buy/Payment/Payment";
import { SafeDataPurgeDays, SUBSCRIPTION_MIN_DATA_LOSS_DAY } from "@/define";

const i18nBase = "SubscriptionInformationComponent";

export async function getSubscriptionsForDisplay() {
  const subscriptionsStore = useSubscriptionsStore();
  await subscriptionsStore.populateSubscriptions();
  subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);
  sortSubscriptions("sku");
  processNotifications();
}

/**
 * Add any post data retrieval processing for each sub
 */
export function processData(subscriptions: ISubscription[]): ISubscription[] {
  const data = processSubscriptionDataForDisplay(subscriptions);
  manageAutoRenewUrl();
  return data ?? null;
}

export function manageAutoRenewUrl() {
  const subscriptionPageStore = useSubscriptionPageStore();
  //cSpell:ignore autorenew
  // for url /subscriptions/computerId=$computerId$&action=autorenew will open autorenew model for the matching computer/subscription
  if (router.currentRoute.value.query.computerId) {
    const computerId: number = parseInt(router.currentRoute.value.query.computerId.toString());
    if (computerId > 0 && router.currentRoute.value.query.action?.toString().toLowerCase() === "autorenew") {
      subscriptionPageStore.computerIdToAutoRenew = computerId;
    }
  }
}

export function sortSubscriptions(field: string) {
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  const { sortDirection } = subscriptionPageStore;

  //turn off sorting indicator on old column
  if (subscriptionPageStore.sortColumn !== field) sortDirection[subscriptionPageStore.sortColumn] = "";

  //pick a direction for the sort
  if (sortDirection[field] === "ascend") sortDirection[field] = "descend";
  else if (sortDirection[field] === "descend") sortDirection[field] = "ascend";
  else sortDirection[field] = "ascend";

  const order = sortDirection[field] === "ascend" ? 1 : -1;

  if (subscriptionsStore.subscriptions)
    subscriptionsStore.subscriptions = subscriptionsStore.subscriptions.sort((a: ISubscription, b: ISubscription) => {
      if (a[field] > b[field]) return order;
      if (a[field] < b[field]) return -order;
      return 0;
    });

  //remember the column being sorted
  subscriptionPageStore.sortColumn = field;
}

export function sortSubscriptionsExpanded(
  field: string,
  direction: Record<string, string>,
  subscriptions: ISubscription[]
) {
  //loop over all keys in the direction object and turn off the sorting indicator for the old column
  for (const key in direction) {
    if (key !== field) direction[key] = "";
  }

  //pick a direction for the sort
  if (direction[field] === "ascend") direction[field] = "descend";
  else if (direction[field] === "descend") direction[field] = "ascend";
  else direction[field] = "ascend";

  const order = direction[field] === "ascend" ? 1 : -1;

  if (subscriptions) {
    subscriptions = subscriptions.sort((a: ISubscription, b: ISubscription) => {
      if (a[field] > b[field]) return order;
      if (a[field] < b[field]) return -order;
      return 0;
    });
  }
  return direction;
}

export async function showAutoRenewSuccess(sub: ISubscription) {
  const subscriptionsStore = useSubscriptionsStore();
  const notificationsStore = useNotificationsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  logEvent("showAutoRenewSuccess", i18nBase);
  notificationsStore.clearNotifications();

  try {
    subscriptionPageStore.showSpinner = true;
    //If actions happen we need to force the store to refresh so we are't left with stale data
    await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
    subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);
    subscriptionPageStore.showSpinner = false;

    const request: IProductDisplayRequest = {
      sku: sub?.sku || sub?.licenseCategoryName || "",
      webrootKeycode: sub.keyCode ?? undefined,
      isTrial: sub.isTrial,
    };

    const isAutoRenewEnabled = subscriptionsStore.subscriptions.find(
      s => s.zuoraSubscriptionNumber === sub.zuoraSubscriptionNumber
    )?.isAutoRenewEnabled;

    const productDisplayName = i18n.global.t(getProductDisplayNameBySku(request)).toString();
    if (isAutoRenewEnabled) {
      notificationsStore.addNotification({
        type: "AutoRenewTurnOnSuccess",
        params: { carbonitePlan: productDisplayName },
      });
    } else {
      notificationsStore.addNotification({
        type: "AutoRenewTurnOffSuccess",
        params: { carbonitePlan: productDisplayName },
      });
    }
  } catch (err) {
    logException(err as Error, `${i18nBase}_showAutoRenewSuccess, Subscription: ${sub.zuoraSubscriptionNumber ?? ""}`);
  }
}

export function manageOutstandingInvoiceUrl() {
  const subscriptionsStore = useSubscriptionsStore();
  const queryString = router.currentRoute.value.query;
  //cSpell:ignore outstandinginvoice
  //if user has outstanding invoice then url /subscriptions?action=outstandinginvoice&paymentMethod=add will navigate directly to invoice details section
  if (
    subscriptionsStore.hasOutstandingInvoice &&
    queryString.action?.toString().toLowerCase() === "outstandinginvoice"
  ) {
    //Check if this is the return from PayPal because we need to set the current subscription
    const request = sessionStorage.getItem("PayPal_OutstandingInvoice_Request");
    if (request) {
      const parsedRequest = JSON.parse(request) as IPayOutstandingInvoicesRequest;
      //Try to find a sub that matches an invoice in the pay off request
      subscriptionsStore.selectedSubscription = subscriptionsStore.subscriptions.find(l =>
        parsedRequest?.invoiceData?.find(i => i.invoiceNumber === l?.outstandingInvoice?.invoiceNumber)
      );
    }

    setPageState(subscriptionPageState.invoiceDetails); //TODO: Update to child route maybe?
  }
}

export async function getConsumerVatInfo() {
  const subscriptionPageStore = useSubscriptionPageStore();
  try {
    logEvent("getConsumerVatInfo", i18nBase);
    subscriptionPageStore.vatInfo = (await unifiedApi.getConsumerVatInfoAsync(null)).data;
  } catch (err) {
    handleApiError(err as AxiosError);
  }
}

export async function setPaymentMethods() {
  logEvent("setPaymentMethods", i18nBase);
  try {
    const response: IGetPaymentMethodsResponse = (await unifiedApi.getUserPaymentMethods()).data;
    const subscriptionPageStore = useSubscriptionPageStore();
    subscriptionPageStore.paymentMethods = response.paymentMethods;
    subscriptionPageStore.defaultCreditCard =
      subscriptionPageStore.paymentMethods !== null && subscriptionPageStore.paymentMethods.length > 0
        ? (subscriptionPageStore.paymentMethods.find(card => card.defaultPaymentMethod) ?? null)
        : null;
  } catch (err) {
    handleApiError(err as AxiosError);
  }
}

export async function saveApplyActivationCodeModal(response: IActivationCodeDialogResponse) {
  const siteSettingsStore = useSiteSettingsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  const notificationsStore = useNotificationsStore();
  const subscriptionsStore = useSubscriptionsStore();

  try {
    subscriptionPageStore.showSpinner = true;
    if (response?.type === "existingSub") {
      subscriptionPageStore.showSpinner = true;
      subscriptionsStore.forceRefresh();
      await getSubscriptionsForDisplay();
      notificationsStore.addNotification({ type: "ActivationCodeAppliedSuccess" });
    } else if (response?.type === "newSub") {
      //Applied to new computer, go to backup page to download the installer
      siteSettingsStore.activationCodeAppliedSuccess = true;
      siteSettingsStore.activationCodeToInstall = response?.activationCode;
      await getUserDetailsFromStore(true);
      router.push("/backup");
    }
    subscriptionPageStore.showSpinner = false;

    toggleApplyActivationCodeModal("saveApplyActivationCodeModal");
    await reloadSubscriberInfo();
  } catch (err) {
    handleApiError(err as AxiosError);
  }
}

export function toggleApplyActivationCodeModal(type: string | null) {
  const subscriptionPageStore = useSubscriptionPageStore();
  type = type || "modal";
  logEvent(
    "toggleApplyActivationCodeModal",
    i18nBase,
    `${subscriptionPageStore.showApplyActivationCodeModal ? "Hiding" : "Showing"} via ${type}`
  );
  return (subscriptionPageStore.showApplyActivationCodeModal = !subscriptionPageStore.showApplyActivationCodeModal);
}

export function toggleAddVatNumberModal(type: string | null) {
  const subscriptionPageStore = useSubscriptionPageStore();
  type = type || "modal";
  logEvent(
    "toggleAddVatNumberModal",
    i18nBase,
    `${subscriptionPageStore.showAddVatNumberModal ? "Hiding" : "Showing"} via ${type}`
  );
  return (subscriptionPageStore.showAddVatNumberModal = !subscriptionPageStore.showAddVatNumberModal);
}

export function addClassToRow(subscription: ISubscription): string {
  let className = "";
  if (showDanger(subscription)) {
    className = "row-danger";
  } else if (showWarning(subscription)) {
    className = "row-warning";
  }

  return className;
}

export function renewSafe(sub: ISubscription) {
  if (showOutstandingInvoiceSection(sub)) {
    goToOutstandingInvoicePage(sub);
    return;
  }
  const routeName = getBuyRouteName();

  router.push({
    name: routeName,
    query: {
      step: pageStates.payment,
      deviceId: sub.computer?.id,
      subscriptionNumber: sub.zuoraSubscriptionNumber,
    },
  });
}

export function showOutstandingInvoiceSection(subscription: ISubscription) {
  return subscription.hasOutstandingInvoices && subscription.outstandingInvoice;
}

export function goToOutstandingInvoicePage(sub: ISubscription) {
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  subscriptionsStore.selectedSubscription = sub;
  subscriptionPageStore.pageState = subscriptionPageState.invoiceDetails; //TODO: update to child route?
}

export async function setPageState(state: string) {
  const notificationsStore = useNotificationsStore();
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  const userStore = useUserStore();

  notificationsStore.clearNotifications();
  subscriptionPageStore.pageState = subscriptionPageState[state];
  logEvent("page state change", subscriptionPageState[state]);
  //if coming back to default page then show banners just like at the start of page.
  if (state === subscriptionPageState.default) {
    processNotifications();
  } else if (state === subscriptionPageState.cancelSubscriptionFailure) {
    notificationsStore.addNotification({ type: "CancelSubscriptionFailure" });
    subscriptionPageStore.pageState = subscriptionPageState.default;
    logEvent("page state change", subscriptionPageState.default);
  } else if (state === subscriptionPageState.cancelSubscriptionSuccess) {
    logEvent("cancelSubscriptionSuccess");
    subscriptionPageStore.showSpinner = true;
    try {
      //If actions happen we need to force the store to refresh so we are't left with stale data
      await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
      subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);
      subscriptionPageStore.showSpinner = false;
      notificationsStore.addNotification({ type: "CancelSubscriptionSuccess" });
      subscriptionPageStore.pageState = subscriptionPageState.default;
      logEvent("page state change", subscriptionPageState.default);
    } catch (err) {
      logException(err as Error, `${i18nBase}_setPageState, User: ${userStore?.currentUser?.personId ?? ""}`);
    }
  }
}

export function setSelectedSubscription(sub: ISubscription) {
  const subscriptionsStore = useSubscriptionsStore();
  const buyFlowStore = useBuyFlowStore();
  subscriptionsStore.selectedSubscription = sub;
  buyFlowStore.openTextBrand = subscriptionsStore.selectedSubscriptionBrand;
}

export function buyNowSafe(subscription: ISubscription, sku: string | undefined = undefined) {
  if (showOutstandingInvoiceSection(subscription)) {
    setPageState("invoiceDetails");
    return;
  }

  buySafe(subscription, sku);
}

//Used for trials and upgrades. Goes to new VD buyflow
export function buySafe(sub: ISubscription, sku?: string) {
  const buyFlowStore = useBuyFlowStore();

  buyFlowStore.changingFromMonthly = sub.isEvergreenMonthly;

  router.push({
    name: "selectPlan",
    query: {
      sku: sku ?? "",
      deviceId: sub.computer?.id,
      subscriptionNumber: sub.zuoraSubscriptionNumber,
    },
  });
}

export function showAutoRenewFailed() {
  const notificationsStore = useNotificationsStore();
  logEvent("AutoRenewTurnOnOrOffFailure");
  notificationsStore.clearNotifications();
  notificationsStore.addNotification({ type: "AutoRenewTurnOnOrOffFailure" });
}

export function handleMoveFromMRtoAR(sub: ISubscription) {
  const subscriptionPageStore = useSubscriptionPageStore();
  logEvent("page state change", subscriptionPageState.movePaymentMethodMRToAR);
  subscriptionPageStore.pageState = subscriptionPageState.movePaymentMethodMRToAR;
  //this needs to be set in case the user wants to add a new CC
  setAddPaymentPreviousState(subscriptionPageState.movePaymentMethodMRToAR);
  setSelectedSubscription(sub);
}

export function setAddPaymentPreviousState(state: string) {
  const subscriptionPageStore = useSubscriptionPageStore();
  subscriptionPageStore.addPaymentPreviousState = subscriptionPageState[state];
}

export function handleChangePaymentMethod() {
  setPageStateFromPaymentDetails("addPaymentMethod", "paymentMethodDetails");
}

export function setPageStateFromPaymentDetails(state: string, previousState: string) {
  setPageState(state);
  setAddPaymentPreviousState(previousState);
}

export async function showAutoRenewDiscountEnabledNotification(sub: ISubscription) {
  const notificationsStore = useNotificationsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  const subscriptionsStore = useSubscriptionsStore();

  logEvent("showAutoRenewDiscountEnabledNotification", i18nBase);
  notificationsStore.clearNotifications();
  try {
    subscriptionPageStore.showSpinner = true;
    //If actions happen we need to force the store to refresh so we are't left with stale data
    await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
    subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);
    const refreshedSubscription = subscriptionsStore.subscriptions.find(
      l => l.zuoraSubscriptionNumber === sub.zuoraSubscriptionNumber
    );
    subscriptionPageStore.showSpinner = false;
    notificationsStore.addNotification({
      type: "AutoRenewDiscountEnabled",
      params: { renewalDiscount: refreshedSubscription?.renewalDiscount },
    });
  } catch (err) {
    logException(
      err as Error,
      `${i18nBase}_showAutoRenewDiscountEnabledNotification, Subscription: ${sub.zuoraSubscriptionNumber ?? ""}`
    );
  }
}

export function addCard(isEditPayment = false) {
  const subscriptionPageStore = useSubscriptionPageStore();
  if (isEditPayment) {
    subscriptionPageStore.pageState = subscriptionPageState.editPaymentMethod;
  } else {
    subscriptionPageStore.pageState = subscriptionPageState.addPaymentMethod;
  }
}

export function moveToOutstandingInvoice(sub: ISubscription) {
  const subscriptionsStore = useSubscriptionsStore();
  if (showOutstandingInvoiceSection(sub)) {
    subscriptionsStore.selectedSubscription = sub;
    setPageState("invoiceDetails");
    return;
  }
}

export async function cancelAddPayment(state: string) {
  sessionStorage.removeItem("product_name_for_auto_renew");
  await setPageState(state);
}

export function setPaymentType(paymentType: paymentTypes) {
  const subscriptionPageStore = useSubscriptionPageStore();
  subscriptionPageStore.currentPaymentType = paymentType;
}

export async function initiatePayPalRequest() {
  const buyFlowStore = useBuyFlowStore();
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();

  buyFlowStore.showProcessingOrderMessage = true;
  logEvent("Initiating paypal transaction", i18nBase);
  try {
    const paypalAgreementTokenResponse = await createPayPalAgreementToken(buyFlowStore.openTextBrand);
    if (paypalAgreementTokenResponse && subscriptionsStore.selectedSubscription != null) {
      logEvent("Got the paypal agreement token, redirecting to paypal approval url", i18nBase);
      //set values to auto renew store
      const autoRenewRequest: IAutoRenewRequest = {
        allowAutoRenew:
          subscriptionPageStore.pageState === subscriptionPageState.editPaymentMethod
            ? subscriptionsStore.selectedSubscription?.isAutoRenewEnabled
            : !subscriptionsStore.selectedSubscription?.isAutoRenewEnabled,
        zuoraSubscriptionNumber: subscriptionsStore.selectedSubscription?.zuoraSubscriptionNumber,
        //paymentMethodId: This will be set in the paypal approval once redirect back from paypal portal
        //reason:  //TODO,
        //reasonCode:  //TODO,
      };
      // indicate that this is an edit payment, not an auto renew
      sessionStorage.setItem(
        "isEditPayment",
        subscriptionPageStore.pageState === subscriptionPageState.editPaymentMethod ? "true" : "false"
      );
      // store product name so the notification has access to it
      sessionStorage.setItem(
        "product_name_for_auto_renew",
        subscriptionsStore.selectedSubscription?.productDisplayName ?? ""
      );
      //store in session since the redirect to paypal doesn't persist data in pinia store
      sessionStorage.setItem("subscription_autoRenewRequest", JSON.stringify(autoRenewRequest));
      //set paypal checkout from page in since the redirect to paypal doesn't persist data in pinia store
      sessionStorage.setItem("PayPal_CheckOutPage", payPalCheckOutPage.SubscriptionInformation);
      //set current subscription brand since the redirect to paypal doesn't persist data in pinia store
      sessionStorage.setItem("Current_Subscription_Brand", buyFlowStore.openTextBrand);

      //set paypal token id since the redirect to paypal doesn't persist data in pinia store
      sessionStorage.setItem("PayPal_token_id", paypalAgreementTokenResponse.tokenId);
      window.location.href = paypalAgreementTokenResponse.approvalUrl;
    }
  } catch (error) {
    handleApiError(error as AxiosError, true);
  } finally {
    buyFlowStore.showProcessingOrderMessage = false;
  }
}

export async function editPaymentMethodHandler(sub: ISubscription, paymentMethodId: string) {
  const buyFlowStore = useBuyFlowStore();
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  const notificationsStore = useNotificationsStore();

  logEvent("editPaymentMethodHandler", i18nBase);
  try {
    const updateAutoRenewOptionRequest: IAutoRenewUpdateRequest = {
      allowAutoRenew: sub.isAutoRenewEnabled,
      surveyChoice: "",
      zuoraSubscriptionNumber: sub.zuoraSubscriptionNumber,
      surveyChoiceCode: null,
      paymentMethodId: paymentMethodId,
    };

    await unifiedApi.updateAutoRenewOption(updateAutoRenewOptionRequest);
    //If actions happen we need to force the store to refresh so we are't left with stale data
    await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
    subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);
    notificationsStore.clearNotifications();
    buyFlowStore.showProcessingOrderMessage = false;
    subscriptionPageStore.pageState = subscriptionPageState.default;
    logEvent("page state change", subscriptionPageState.default);
    setPaymentMethods();
    notificationsStore.addNotification({ type: "AddCreditCardSuccess" });
    subscriptionPageStore.pageState = subscriptionPageState.default;
  } catch (error) {
    buyFlowStore.showProcessingOrderMessage = false;
    logException(
      error as Error,
      `${i18nBase}_editPaymentMethodHandler, Subscription: ${sub.zuoraSubscriptionNumber ?? ""}`
    );
  }
}

export async function addPaymentMethodHandler(sub: ISubscription, paymentMethodId: string) {
  logEvent("addPaymentMethodHandler", i18nBase);
  const buyFlowStore = useBuyFlowStore();
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  const notificationsStore = useNotificationsStore();
  notificationsStore.clearNotifications();
  if (
    subscriptionPageStore.pageState === subscriptionPageState.movePaymentMethodMRToAR ||
    subscriptionPageStore.addPaymentPreviousState === subscriptionPageState.movePaymentMethodMRToAR
  ) {
    try {
      const updateAutoRenewOptionRequest: IAutoRenewUpdateRequest = {
        allowAutoRenew: !sub.isAutoRenewEnabled,
        surveyChoice: "",
        zuoraSubscriptionNumber: sub.zuoraSubscriptionNumber,
        surveyChoiceCode: null,
        paymentMethodId: paymentMethodId,
      };

      await unifiedApi.updateAutoRenewOption(updateAutoRenewOptionRequest);
      //If actions happen we need to force the store to refresh so we are't left with stale data
      await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
      subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);

      const request: IProductDisplayRequest = {
        sku: sub.sku,
        webrootKeycode: sub.keyCode ?? undefined,
        isTrial: sub.isTrial,
      };

      const productDisplayName = i18n.global.t(getProductDisplayNameBySku(request)).toString();

      notificationsStore.addNotification({
        type: "MovePaymentMethodMRToAR",
        params: { carbonitePlan: productDisplayName },
      });
    } catch (error) {
      buyFlowStore.showProcessingOrderMessage = false;
      logException(
        error as Error,
        `${i18nBase}_addPaymentMethodHandler, Subscription: ${sub.zuoraSubscriptionNumber ?? ""}`
      );
      notificationsStore.addNotification({ type: "AutoRenewTurnOnOrOffFailure" });
    }
  } else {
    //If actions happen we need to force the store to refresh so we aren't left with stale data
    await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
    subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);
    notificationsStore.addNotification({ type: "AddCreditCardSuccess" });
    //after success , update the vat info section as well
    await getConsumerVatInfo();
  }
  buyFlowStore.showProcessingOrderMessage = false;
  subscriptionPageStore.pageState = subscriptionPageState.default;
  subscriptionPageStore.addPaymentPreviousState = subscriptionPageState.default;
  logEvent("page state change", subscriptionPageState.default);
  setPaymentMethods();
}

export async function enableAutoRenew(paymentMethodId: string) {
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  try {
    if (subscriptionsStore.selectedSubscription !== undefined) {
      subscriptionPageStore.showSpinner = true;
      const updateAutoRenewOptionRequest: IAutoRenewUpdateRequest = {
        allowAutoRenew: true,
        zuoraSubscriptionNumber: subscriptionsStore.selectedSubscription.zuoraSubscriptionNumber,
        surveyChoice: "",
        surveyChoiceCode: null,
        paymentMethodId: paymentMethodId,
      };
      const updated = (await unifiedApi.updateAutoRenewOption(updateAutoRenewOptionRequest)).data;
      if (updated) {
        await getUserDetailsFromStore(true);
        await showAutoRenewSuccess(subscriptionsStore.selectedSubscription);
      }
    }
  } catch (error) {
    handleApiError(error as AxiosError);
  } finally {
    subscriptionPageStore.pageState = subscriptionPageState.default;
    subscriptionPageStore.showSpinner = false;
  }
}

export async function handleSuccess() {
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionPageStore = useSubscriptionPageStore();
  const notificationsStore = useNotificationsStore();

  notificationsStore.addNotification({ type: "RenewSuccessMessage", dismissible: false });
  subscriptionPageStore.pageState = subscriptionPageState.default;
  logEvent("page state change", subscriptionPageState.default);
  //If actions happen we need to force the store to refresh so we are't left with stale data
  await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
  subscriptionsStore.subscriptions = processData(subscriptionsStore.subscriptions);
}

export async function updatePaymentMethods(updatedCreditCards: IPaymentMethod[]) {
  const subscriptionPageStore = useSubscriptionPageStore();
  subscriptionPageStore.paymentMethods = updatedCreditCards;
}

export async function addVatNumber(response: string) {
  logEvent("addVatNumber", i18nBase);
  if (response === "OK") {
    //reload vat info data
    await getConsumerVatInfo();
    refreshVatInfoSection();
  } else {
    logEvent("Failure in adding Vat Number", JSON.stringify(response));
  }

  toggleAddVatNumberModal("addVatNumber");
}

export function refreshVatInfoSection() {
  const subscriptionPageStore = useSubscriptionPageStore();
  subscriptionPageStore.vatInfoChanged = false;
  setTimeout(() => {
    subscriptionPageStore.vatInfoChanged = true;
  }, 10);
}

export async function removeVatNumber() {
  const subscriptionPageStore = useSubscriptionPageStore();
  const userStore = useUserStore();

  subscriptionPageStore.isRemovingVat = true;

  try {
    const vatInfoResponse = (await unifiedApi.setVatInfo("", "")).data;
    if (vatInfoResponse.success) {
      if (subscriptionPageStore.vatInfo) subscriptionPageStore.vatInfo.vatNumber = null;
      refreshVatInfoSection();

      //reload vat info data
      await getConsumerVatInfo();
    } else {
      logEvent("Failure in removing Vat Number", vatInfoResponse.responseMessage);
    }
  } catch (err) {
    logException(err as Error, `${i18nBase}_removeVatNumber, User: ${userStore?.currentUser?.personId ?? ""}`);
  }
  subscriptionPageStore.isRemovingVat = false;
}

export function subscriptionIsNotExpired(sub: ISubscription) {
  const isSafeSKU = isSafeProduct(sub);

  if ((sub.isEvergreenMonthly && !sub?.isCancelledEvergreenSubscription) || sub?.isActiveUntilCancelled) {
    return true;
  }

  //Check if sub has an outstanding invoice
  if (sub?.hasOutstandingInvoices || sub?.outstandingInvoice) {
    return false;
  }

  if (sub?.zuoraSubscriptionStatus === ZuoraSubscriptionStatus.Cancelled) {
    return false;
  }

  if (isSafeSKU) {
    return sub?.remainingDays >= 0 || sub?.isAutoRenewEnabled;
  } else {
    return sub?.remainingDays >= 0 || sub?.isAutoRenewEnabled || isNotActiveYet(sub);
  }
}

// Determines if a subscription is not renewable based on its status and type
export function subscriptionIsNotRenewable(sub: ISubscription) {
  const subscriptionsStore = useSubscriptionsStore();
  if (hideRenewOrUpgradeLinkForLimitedPurchaseSub(sub, subscriptionsStore.subsWithoutExpiredTrials)) {
    return true;
  }

  //Safe Subscriptions
  if (sub.keyCodeSellerType === KeyCodeSeller.Safe) {
    if (sub?.keyCode) {
      //Long expired safe keycodes can not be renewed
      return sub.remainingDays <= SUBSCRIPTION_MIN_DATA_LOSS_DAY;
    }
    //Purged or in final warning state (more than -60 days expired)
    return (
      sub?.expiration === ExpirationState.FinalWarning ||
      sub?.expiration === ExpirationState.Purged ||
      sub?.remainingDays <= SafeDataPurgeDays.Direct
    );
  }

  return hideExpiredTrial(sub);
}
